import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  devolverPagamento, getPagamentosDoPlano, getPainelDosPlanos, IdDoPlano,
  PagamentoDoPlano, PainelDosPlanos, PessoaComPlano,
} from '../services/api';
import { useConfirmar } from '../contexts/Confirmacao';

/**
 * Os planos no Monitor (20/10) — `docs/designs/precificacao.md`.
 *
 * - O contador "Completo: N de 6": com 6 clientes no Completo, o plano PJ do
 *   provedor se paga, e é hora de migrar (decisão dela).
 * - Quem libera é o pagamento do Asaas. O "Recebi Pix fora do Asaas" (origem
 *   'manual') saiu em 23/10; os pagamentos manuais antigos continuam na
 *   lista e se devolvem daqui.
 * - Devolver: só DEPOIS de o dinheiro voltar. O botão mostra o que a regra
 *   permite: até o dia 7, só o valor inteiro (a lei); do 8º ao 30º, no
 *   primeiro pagamento, ela escolhe metade ou tudo.
 *
 * ── O que ela viu em 23/10 ───────────────────────────────────────────────
 *
 * "Registrei a devolução e o botão fez nada": o resultado aparecia no FIM da
 * seção, e quem teve o primeiro pagamento devolvido some da lista (encerrou).
 * Agora o recado fica sob o título e a tela rola até ele.
 */

const reais = (c: number): string => `R$ ${(c / 100).toFixed(2).replace('.', ',')}`;
const NOMES: Record<IdDoPlano, string> = { essencial: 'Essencial', essencial_anual: 'Anual', completo: 'Completo' };
const quem = (p: PessoaComPlano): string => p.nome || p.email || 'conta sem e-mail';
const SOBRE_FUNDADOR = 'Uma das primeiras pessoas a pagar. Mantém o preço de hoje enquanto renovar sem interromper; '
  + 'se o plano encerrar (devolução, ou 3 dias vencido), perde.';
const PlanosNoMonitor: React.FC = () => {
  const confirmar = useConfirmar();
  const [painel, setPainel] = useState<PainelDosPlanos | null>(null);
  const [abertos, setAbertos] = useState<Record<string, PagamentoDoPlano[] | 'carregando'>>({});
  const [agindo, setAgindo] = useState<string | null>(null);
  const [recado, setRecado] = useState<string | null>(null);
  const recadoRef = useRef<HTMLParagraphElement>(null);

  // O recado mora sob o título; quem clicou está lá embaixo na lista.
  useEffect(() => {
    if (recado) recadoRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [recado]);

  const carregar = useCallback(() => getPainelDosPlanos().then(setPainel).catch(() => setPainel(null)), []);
  useEffect(() => { void carregar(); }, [carregar]);

  const verPagamentos = async (userId: string) => {
    if (abertos[userId] && abertos[userId] !== 'carregando') {
      setAbertos(a => { const b = { ...a }; delete b[userId]; return b; });
      return;
    }
    setAbertos(a => ({ ...a, [userId]: 'carregando' }));
    try {
      const ps = await getPagamentosDoPlano(userId);
      setAbertos(a => ({ ...a, [userId]: ps }));
    } catch {
      setAbertos(a => { const b = { ...a }; delete b[userId]; return b; });
      setRecado('Não consegui ler os pagamentos.');
    }
  };

  const devolver = async (p: PessoaComPlano, pg: PagamentoDoPlano, valor: number) => {
    const encerra = pg.primeiro;
    if (!await confirmar({
      titulo: `Registrar devolução de ${reais(valor)}?`,
      alvo: quem(p),
      // Pelo Asaas, é ESTE botão que pede o estorno (`monitor.ts`). O texto
      // antigo mandava estornar lá antes, e aí o botão tentava de novo.
      texto: (pg.origem === 'manual'
        ? `Faça o Pix de volta${pg.chavePix ? ` para ${pg.chavePix}` : ''} ANTES de confirmar. `
        : 'Este botão faz o estorno pelo Asaas. Não estorne por lá antes. ')
        + (encerra
          ? 'Era o primeiro pagamento: o acesso encerra' + (pg.plano === 'completo' ? ' e o banco é desconectado.' : '.')
          : `Era uma renovação: o vencimento volta ${pg.dias} dias.`),
      acao: 'Registrar devolução',
      perigo: true,
    })) return;
    setAgindo(p.userId); setRecado(null);
    try {
      const r = await devolverPagamento(pg.id, valor);
      setRecado(
        `${quem(p)}: devolução de ${reais(valor)} registrada`
        + (pg.origem === 'asaas' ? ' e estornada no Asaas' : '')
        + (r.encerra ? '. O plano encerrou, e a pessoa saiu da lista.'
          : pg.tipo === 'troca' ? '. Era a diferença de subida: o vencimento não muda.'
          : `. O vencimento voltou ${pg.dias} dias.`)
        + (r.aviso ? ` ${r.aviso}` : ''),
      );
      setAbertos(a => { const b = { ...a }; delete b[p.userId]; return b; });
      await carregar();
    } catch (e: any) {
      setRecado(`${quem(p)}: ${e?.response?.data?.error ?? 'Não consegui registrar a devolução.'}`);
    } finally {
      setAgindo(null);
    }
  };

  if (!painel || !painel.disponivel) return null;

  const pedidos = painel.pessoas.filter(p => p.estado === 'pedido');
  const dentro = painel.pessoas.filter(p => p.estado === 'liberado');

  const linha = (p: PessoaComPlano) => {
    const pgs = abertos[p.userId];
    return (
      <li key={p.userId}>
        <div className="mon-suporte-topo">
          <span className="mon-pedido-quem">
            <strong>{p.nome || p.email || 'conta sem e-mail'}</strong>
            {p.nome && p.email && <span>{p.email}</span>}
          </span>
          <span className="mon-suporte-quando">
            {NOMES[p.plano]}{p.fundador && <span title={SOBRE_FUNDADOR}> · fundador</span>}
            {p.liberadoAte ? ` · até ${p.liberadoAte.slice(8, 10)}/${p.liberadoAte.slice(5, 7)}` : ''}
          </span>
        </div>
        {p.estado === 'liberado' && (
          <p className="mon-nota">
            {(p.diasRestantes ?? 0) < 0
              ? `Venceu há ${-(p.diasRestantes ?? 0)} ${p.diasRestantes === -1 ? 'dia' : 'dias'}.`
              : `${p.diasRestantes} ${p.diasRestantes === 1 ? 'dia restante' : 'dias restantes'}.`}
            {p.plano === 'completo' && ` ${p.bancos} de ${p.maxBancos} ${p.maxBancos === 1 ? 'banco' : 'bancos'}.`}
            {p.planoProximo && ` Troca para ${NOMES[p.planoProximo]} no vencimento.`}
          </p>
        )}
        <div className="mon-suporte-acoes">
          <button onClick={() => verPagamentos(p.userId)} disabled={agindo !== null}>
            {pgs && pgs !== 'carregando' ? 'Fechar pagamentos' : 'Pagamentos'}
          </button>
        </div>
        {pgs === 'carregando' && <p className="mon-nota">Carregando…</p>}
        {Array.isArray(pgs) && (
          pgs.length === 0
            ? <p className="mon-nota">Nenhum pagamento registrado.</p>
            : (
              <ul className="mon-suporte">
                {pgs.map(pg => (
                  <li key={pg.id}>
                    <p className="mon-nota">
                      {pg.pagoEm.slice(8, 10)}/{pg.pagoEm.slice(5, 7)} · {NOMES[pg.plano]} · {reais(pg.valorCentavos)}
                      {' '}· {pg.origem === 'asaas' ? 'Asaas' : 'à mão'}{pg.primeiro ? ' · primeiro' : ''}
                      {pg.tipo === 'troca' && ' · diferença de subida'}
                      {pg.devolvidoEm && ` · devolvido ${reais(pg.valorDevolvidoCentavos ?? 0)} em ${pg.devolvidoEm.slice(8, 10)}/${pg.devolvidoEm.slice(5, 7)}`}
                    </p>
                    {pg.devolucaoPedidaEm && !pg.devolvidoEm && (
                      <p className="mon-nota"><strong>Pediu devolução</strong> ao cancelar, em {new Date(pg.devolucaoPedidaEm).toLocaleDateString('pt-BR')}. Decida abaixo.</p>
                    )}
                    {!pg.devolvidoEm && pg.devolucao.tipo === 'obrigatoria' && (
                      <div className="mon-suporte-acoes">
                        <button onClick={() => devolver(p, pg, pg.devolucao.tipo === 'obrigatoria' ? pg.devolucao.centavos : 0)} disabled={agindo !== null}>
                          Devolver {reais(pg.devolucao.centavos)} (dia {pg.devolucao.dias}: a lei manda devolver tudo)
                        </button>
                      </div>
                    )}
                    {!pg.devolvidoEm && pg.devolucao.tipo === 'escolha' && (
                      <div className="mon-suporte-acoes">
                        <button onClick={() => devolver(p, pg, pg.devolucao.tipo === 'escolha' ? pg.devolucao.metadeCentavos : 0)} disabled={agindo !== null}>
                          Devolver metade, {reais(pg.devolucao.metadeCentavos)}
                        </button>
                        <button onClick={() => devolver(p, pg, pg.devolucao.tipo === 'escolha' ? pg.devolucao.tudoCentavos : 0)} disabled={agindo !== null}>
                          Devolver tudo, {reais(pg.devolucao.tudoCentavos)}
                        </button>
                        <span className="mon-nota">dia {pg.devolucao.dias}: você decide</span>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )
        )}
      </li>
    );
  };

  return (
    <section className="mon-secao">
      <h3>Planos</h3>
      <p className="mon-nota">
        Completo: <strong>{painel.completos} de {painel.completosParaMigrar}</strong>
        {painel.completos >= painel.completosParaMigrar ? ' — hora de migrar para o plano PJ do provedor.' : ' para migrar ao plano PJ do provedor.'}
        {' '}Fundadores: {painel.fundadores} de {painel.vagasDeFundador}.
        {!painel.cobrando && ' Cobrança desligada (PLANO_COBRANDO): o app segue aberto para todo mundo.'}
      </p>
      <p className="mon-nota">
        Quem paga pelo Asaas renova sozinho todo mês.
        {' '}<strong>Fundador</strong>: {SOBRE_FUNDADOR.charAt(0).toLowerCase() + SOBRE_FUNDADOR.slice(1)}
      </p>
      {recado && <p ref={recadoRef} className="mon-nota" role="status"><strong>{recado}</strong></p>}
      {pedidos.length > 0 && (
        <>
          <p className="mon-suporte-meta">Escolheram um plano e ainda não pagaram</p>
          <ul className="mon-suporte">{pedidos.map(linha)}</ul>
        </>
      )}
      {dentro.length > 0 && (
        <>
          <p className="mon-suporte-meta">Com plano</p>
          <ul className="mon-suporte">{dentro.map(linha)}</ul>
        </>
      )}
      {pedidos.length === 0 && dentro.length === 0 && <p className="mon-nota">Ninguém escolheu plano ainda.</p>}
    </section>
  );
};

export default PlanosNoMonitor;
